import { DEFAULT_PROJECT_VIEW_MODE, IMAGE_MODE, PROJECT_VIEW_MODE } from '@utils/constants';

// Builds a mode-aware project URL. `mode` is explicit here because, unlike
// internal navigation inside an established ProjectModeProvider, callers at
// this level (project list, cards) have no ambient route mode to derive from.
export const buildProjectUrl = (uuid, mode = DEFAULT_PROJECT_VIEW_MODE, suffix = '') => {
	const base = mode === PROJECT_VIEW_MODE.MODEL ? '/projects' : '/projects/v2';
	return `${base}/${uuid}${suffix ? `/${suffix}` : ''}`;
};

// A project's page from a list, card or link: video projects have their own page (no view modes), every other
// project the given view mode.
export const projectHomeUrl = (uuid, imageMode, mode = DEFAULT_PROJECT_VIEW_MODE, suffix = '') =>
	imageMode === IMAGE_MODE.VIDEO
		? `/video-projects/${uuid}${suffix ? `/${suffix}` : ''}`
		: buildProjectUrl(uuid, mode, suffix);

// The object annotation of Collection view lives in the URL as well, so a reload
// or a shared link lands in it: `annotate` names the object (alone, the area is
// still to be drawn) and `area` carries the drawn boundary.
export const ANNOTATION_SEARCH_PARAMS = Object.freeze({ node: 'annotate', area: 'area' });

// Search params that only mean something in one view mode: `model` is the
// ModelPicker selection, `collection_uuid` the CollectionPicker selection, and
// the annotation pair belongs to Collection view.
export const MODE_SPECIFIC_SEARCH_PARAMS = Object.freeze([
	'model',
	'collection_uuid',
	ANNOTATION_SEARCH_PARAMS.node,
	ANNOTATION_SEARCH_PARAMS.area,
]);

const samePoint = (a, b) => a[0] === b[0] && a[1] === b[1];

/**
 * A boundary (GeoJSON Polygon coordinates in map units, EPSG:3857) as the `area`
 * param: `x,y;x,y;…` in whole metres, the closing point left out. A hand-drawn
 * area is a dozen points at most, so it stays short enough for a link.
 *
 * @returns {string|null} null when the boundary is not a ring of three or more points
 */
export const encodeArea = boundary => {
	const ring = boundary?.[0];
	if (!Array.isArray(ring) || ring.length < 3) return null;
	const open =
		ring.length > 3 && samePoint(ring[0], ring[ring.length - 1]) ? ring.slice(0, -1) : ring;
	if (open.length < 3) return null;
	return open.map(([x, y]) => `${Math.round(x)},${Math.round(y)}`).join(';');
};

/**
 * The boundary of an `area` param, closed again, or null when the text is not
 * three or more numeric points.
 *
 * @returns {number[][][]|null}
 */
export const decodeArea = text => {
	if (typeof text !== 'string' || !text) return null;
	const ring = text.split(';').map(pair => pair.split(',').map(Number));
	const valid =
		ring.length >= 3 && ring.every(point => point.length === 2 && point.every(Number.isFinite));
	if (!valid) return null;
	const closed = samePoint(ring[0], ring[ring.length - 1]) ? ring : [...ring, ring[0]];
	return [closed];
};

// Drops the mode-specific params from a query string (with or without the
// leading `?`) so a project URL can be re-based onto the other view mode
// without carrying a selection that mode can't resolve. Returns `''` or a
// `?`-prefixed string, ready to append to buildProjectUrl().
export const stripModeSearchParams = search => {
	const params = new URLSearchParams(search);
	MODE_SPECIFIC_SEARCH_PARAMS.forEach(param => params.delete(param));
	const rest = params.toString();
	return rest ? `?${rest}` : '';
};
